/* eslint-disable react/destructuring-assignment */
import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Radio,
  Snackbar,
  Tooltip,
  Typography
} from '@material-ui/core';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import QuizRoundedIcon from '@mui/icons-material/QuizRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import SentimentSatisfiedAltRoundedIcon from '@mui/icons-material/SentimentSatisfiedAltRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { motion } from 'framer-motion';
import { questionnaireApi } from '../../../api/questionnaireApi';
import useMounted from '../../../hooks/useMounted';
import EyeIcon from '../../../icons/Eye';
import QuestionnairePreview from '../questionnaire/QuestionnairePreview';
import { alpha } from '@mui/material/styles';
import { getTone, toneAlpha, grey, greyAlpha } from '../../../theme/tones';

// Trois types de questionnaires suivis pour la session.
const PHASES = [
  {
    key: 'pre',
    label: 'Pré-Formation',
    hint: 'Avant la formation',
    color: getTone('emerald').color,
    Icon: AssignmentRoundedIcon
  },
  {
    key: 'post',
    label: 'Post-Formation',
    hint: 'Après la formation',
    color: getTone('orange').color,
    Icon: AssignmentTurnedInRoundedIcon
  },
  {
    key: 'satisfaction',
    label: 'Satisfaction',
    hint: 'Retour des apprenants',
    color: getTone('indigo').base,
    Icon: SentimentSatisfiedAltRoundedIcon
  }
];

const SessionQuestionnairesStatus = (props) => {
  const { sessionId } = props;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const mounted = useMounted();

  // Dialogue « Compléter les manquants »
  const [dialogOpen, setDialogOpen] = useState(false);
  const [candLoading, setCandLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [missingTypes, setMissingTypes] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [creating, setCreating] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  // Aperçu d'un questionnaire
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const getStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await questionnaireApi.getQuestionnairesStatusBySession(
        sessionId,
        localStorage.getItem('accessToken')
      );
      if (mounted.current) {
        setStatus(response && response.status ? response.data : null);
      }
    } catch (err) {
      console.error(err);
      if (mounted.current) setStatus(null);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [sessionId, mounted]);

  useEffect(() => {
    getStatus();
  }, [getStatus]);

  const openCompletion = useCallback(async () => {
    setDialogOpen(true);
    setSelectedSource(null);
    setCandLoading(true);
    try {
      const resp = await questionnaireApi.getCompletionCandidatesBySession(
        sessionId,
        localStorage.getItem('accessToken')
      );
      if (mounted.current) {
        if (resp && resp.status && resp.data) {
          setCandidates(resp.data.candidates || []);
          setMissingTypes(resp.data.missing || []);
        } else {
          setCandidates([]);
          setMissingTypes([]);
        }
      }
    } catch (err) {
      console.error(err);
      if (mounted.current) setCandidates([]);
    } finally {
      if (mounted.current) setCandLoading(false);
    }
  }, [sessionId, mounted]);

  const handleCreate = useCallback(async () => {
    if (!selectedSource) return;
    setCreating(true);
    try {
      const resp = await questionnaireApi.createMissingQuestionnairesFromSession(
        sessionId,
        selectedSource,
        localStorage.getItem('accessToken')
      );
      if (resp && resp.status) {
        setDialogOpen(false);
        setSnack({ open: true, severity: 'success', message: resp.message || 'Questionnaires créés avec succès.' });
        await getStatus();
      } else {
        setSnack({ open: true, severity: 'error', message: (resp && resp.message) || 'Échec de la création.' });
      }
    } catch (err) {
      console.error(err);
      setSnack({ open: true, severity: 'error', message: 'Échec de la création des questionnaires.' });
    } finally {
      if (mounted.current) setCreating(false);
    }
  }, [selectedSource, sessionId, getStatus, mounted]);

  const handleOpenPreview = useCallback(async (questionnaire) => {
    setPreviewData(questionnaire);
    setPreviewQuestions([]);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const response = await questionnaireApi.getQuestions(
        questionnaire.id,
        localStorage.getItem('accessToken')
      );
      if (mounted.current && response && response.status) {
        setPreviewQuestions(response.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (mounted.current) setPreviewLoading(false);
    }
  }, [mounted]);

  const configuredCount = status
    ? PHASES.filter((p) => status[p.key] && status[p.key].exists).length
    : 0;
  const allDone = configuredCount === PHASES.length;
  const ringColor = allDone ? getTone('emerald').color : getTone('indigo').base;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Box
        sx={{
          borderRadius: '24px',
          p: { xs: 2.5, md: 3.5 },
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(180deg, ${grey(0)} 0%, ${grey(50)} 100%)`,
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          boxShadow: (theme) => `0 24px 60px -32px ${alpha(theme.palette.text.primary, 0.35)}`
        }}
      >
        {/* Halo décoratif */}
        <Box
          sx={{
            position: 'absolute',
            top: -120,
            right: -80,
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: (theme) => `radial-gradient(circle, ${alpha(theme.palette.tones.indigo.base, 0.10)} 0%, ${alpha(theme.palette.tones.indigo.base, 0)} 70%)`,
            pointerEvents: 'none'
          }}
        />

        {/* En-tête */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${getTone('indigo').base} 0%, ${getTone('indigo').color} 100%)`,
                boxShadow: (theme) => `0 10px 22px -8px ${alpha(theme.palette.tones.indigo.base, 0.7)}`
              }}
            >
              <QuizRoundedIcon sx={{ color: grey(0), fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.15rem', letterSpacing: '-0.02em' }}>
                Questionnaires de la session
              </Typography>
              <Typography sx={{ color: 'text.disabled', fontSize: '0.8rem', fontWeight: 500 }}>
                Pré-Formation · Post-Formation · Satisfaction
              </Typography>
            </Box>
          </Box>

          {/* Anneau de progression + action */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {!loading && status && configuredCount < PHASES.length && (
              <Button
                onClick={openCompletion}
                variant="contained"
                disableElevation
                startIcon={<AutoFixHighRoundedIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                  borderRadius: '12px',
                  px: 2,
                  color: grey(0),
                  background: `linear-gradient(135deg, ${getTone('indigo').base} 0%, ${getTone('indigo').color} 100%)`,
                  boxShadow: (theme) => `0 10px 22px -10px ${alpha(theme.palette.tones.indigo.base, 0.8)}`,
                  '&:hover': { background: `linear-gradient(135deg, ${getTone('indigo').color} 0%, ${getTone('indigo').deep} 100%)` }
                }}
              >
                Compléter les manquants
              </Button>
            )}
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={58}
                thickness={4.5}
                sx={{ color: grey(100) }}
              />
              <CircularProgress
                variant="determinate"
                value={(configuredCount / PHASES.length) * 100}
                size={58}
                thickness={4.5}
                sx={{
                  color: ringColor,
                  position: 'absolute',
                  left: 0,
                  '& .MuiCircularProgress-circle': { strokeLinecap: 'round' }
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'text.primary' }}>
                  {`${configuredCount}/${PHASES.length}`}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography sx={{ fontWeight: 800, color: allDone ? getTone('emerald').color : 'text.secondary', fontSize: '0.9rem', lineHeight: 1.1 }}>
                {allDone ? 'Complet' : 'Incomplet'}
              </Typography>
              <Typography sx={{ color: 'text.disabled', fontSize: '0.72rem', fontWeight: 600 }}>
                configurés
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tuiles */}
        {loading ? (
          <Box sx={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2.5} sx={{ position: 'relative', zIndex: 1 }}>
            {PHASES.map((p) => {
              const info = status ? status[p.key] : null;
              const exists = !!(info && info.exists);
              const questionnaires = (info && info.questionnaires) || [];
              const PhaseIcon = p.Icon;

              return (
                <Grid item xs={12} sm={6} md={4} key={p.key}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 2.5,
                      borderRadius: '18px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.75,
                      transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                      border: '1px solid',
                      borderColor: exists ? `${p.color}3d` : greyAlpha(300, 0.7),
                      background: exists
                        ? `linear-gradient(165deg, ${p.color}14 0%, ${grey(0)} 62%)`
                        : `linear-gradient(165deg, ${grey(50)} 0%, ${grey(0)} 62%)`,
                      boxShadow: exists
                        ? `0 16px 34px -22px ${p.color}cc`
                        : `0 12px 26px -22px ${greyAlpha(800, 0.5)}`,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: exists
                          ? `0 26px 44px -22px ${p.color}cc`
                          : `0 20px 36px -22px ${greyAlpha(800, 0.5)}`
                      }
                    }}
                  >
                    {/* Ligne icône + statut */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: exists ? `${p.color}1f` : grey(100),
                            color: exists ? p.color : 'text.disabled'
                          }}
                        >
                          <PhaseIcon sx={{ fontSize: 27 }} />
                        </Box>
                        {exists && (
                          <CheckCircleRoundedIcon
                            sx={{
                              position: 'absolute',
                              right: -6,
                              bottom: -6,
                              fontSize: 22,
                              color: p.color,
                              background: (theme) => `${theme.palette.common.white}`,
                              borderRadius: '50%'
                            }}
                          />
                        )}
                      </Box>

                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.1,
                          py: 0.45,
                          borderRadius: '999px',
                          bgcolor: exists ? `${p.color}1a` : toneAlpha('neutral', 0.16),
                          color: exists ? p.color : 'text.disabled'
                        }}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            bgcolor: 'currentColor'
                          }}
                        />
                        <Typography sx={{ fontWeight: 800, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {exists ? 'Existant' : 'Manquant'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Titre */}
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: '1.02rem', letterSpacing: '-0.01em' }}>
                        {p.label}
                      </Typography>
                      <Typography sx={{ color: 'text.disabled', fontSize: '0.76rem', fontWeight: 600 }}>
                        {p.hint}
                      </Typography>
                    </Box>

                    {/* Pastilles ou message d'absence */}
                    {exists ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 'auto' }}>
                        {questionnaires.map((q) => (
                          <Box
                            key={q.id}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.6,
                              maxWidth: '100%',
                              pl: 1,
                              pr: 0.4,
                              py: 0.35,
                              borderRadius: '999px',
                              bgcolor: 'background.paper',
                              border: `1px solid ${p.color}40`,
                              boxShadow: (theme) => `0 1px 2px ${alpha(theme.palette.text.primary, 0.05)}`
                            }}
                          >
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: p.color, flexShrink: 0 }} />
                            <Tooltip title={`${q.type} · #${q.id}`} arrow>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: 'text.primary',
                                  fontSize: '0.78rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {q.name}
                              </Typography>
                            </Tooltip>
                            <Tooltip title="Aperçu du questionnaire" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenPreview(q)}
                                sx={{
                                  ml: 0.25,
                                  p: 0.4,
                                  flexShrink: 0,
                                  color: p.color,
                                  bgcolor: `${p.color}14`,
                                  transition: 'background-color 0.15s ease',
                                  '&:hover': { bgcolor: `${p.color}2e` }
                                }}
                              >
                                <EyeIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          mt: 'auto',
                          px: 1.5,
                          py: 1.25,
                          borderRadius: '12px',
                          border: `1px dashed ${greyAlpha(300, 0.9)}`,
                          bgcolor: greyAlpha(50, 0.6)
                        }}
                      >
                        <Typography sx={{ color: 'text.disabled', fontSize: '0.78rem', fontWeight: 600 }}>
                          Aucun questionnaire associé à cette session
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* Dialogue : compléter les questionnaires manquants */}
      <Dialog
        open={dialogOpen}
        onClose={() => !creating && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
      >
        {/* En-tête du dialogue */}
        <Box
          sx={{
            p: 3,
            position: 'relative',
            color: grey(0),
            background: `linear-gradient(135deg, ${getTone('indigo').base} 0%, ${getTone('indigo').color} 100%)`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 4 }}>
            <AutoFixHighRoundedIcon sx={{ fontSize: 26 }} />
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
                Compléter les questionnaires
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', opacity: 0.88 }}>
                Recopier depuis une session complète de la même formation
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setDialogOpen(false)}
            disabled={creating}
            sx={{ position: 'absolute', top: 10, right: 10, color: grey(0) }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* Types manquants */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 800, mb: 1 }}>
              {`${missingTypes.length} questionnaire(s) manquant(s) — seront créés :`}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {missingTypes.map((k) => {
                const p = PHASES.find((ph) => ph.key === k);
                if (!p) return null;
                const PIcon = p.Icon;
                return (
                  <Box
                    key={k}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.45,
                      borderRadius: '999px',
                      bgcolor: `${p.color}1a`,
                      color: p.color
                    }}
                  >
                    <PIcon sx={{ fontSize: 15 }} />
                    <Typography sx={{ fontWeight: 800, fontSize: '0.72rem' }}>{p.label}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {candLoading ? (
            <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : candidates.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 800, color: 'text.secondary' }}>
                Aucune session modèle disponible
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', mt: 0.5 }}>
                Aucune autre session de cette formation ne possède les trois questionnaires
                pour servir de modèle.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 800, mb: 1 }}>
                Choisissez la session modèle :
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {candidates.map((c) => {
                  const selected = String(selectedSource) === String(c.session_id);
                  return (
                    <Box
                      key={c.session_id}
                      onClick={() => setSelectedSource(c.session_id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.25,
                        borderRadius: '14px',
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: selected ? getTone('indigo').base : grey(200),
                        bgcolor: selected ? toneAlpha('indigo', 0.06) : grey(0),
                        transition: 'all 0.15s ease',
                        '&:hover': { borderColor: getTone('indigo').base, bgcolor: (theme) => alpha(theme.palette.tones.indigo.base, 0.04) }
                      }}
                    >
                      <Radio
                        checked={selected}
                        onChange={() => setSelectedSource(c.session_id)}
                        size="small"
                        sx={{ p: 0.5, color: grey(300), '&.Mui-checked': { color: getTone('indigo').base } }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.9rem' }}>
                          {`Session n°${c.number_session}`}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {PHASES.map((p) => {
                            const t = c.types ? c.types[p.key] : null;
                            if (!t) return null;
                            return (
                              <Box
                                key={p.key}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  px: 0.9,
                                  py: 0.3,
                                  borderRadius: '999px',
                                  bgcolor: `${p.color}14`,
                                  border: `1px solid ${p.color}30`,
                                  maxWidth: '100%'
                                }}
                              >
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: p.color, flexShrink: 0 }} />
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    color: 'text.secondary',
                                    maxWidth: 150,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {t.name}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={creating}
            sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disableElevation
            disabled={!selectedSource || creating || candidates.length === 0}
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighRoundedIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: '12px',
              px: 2.5,
              color: grey(0),
              background: `linear-gradient(135deg, ${getTone('indigo').base} 0%, ${getTone('indigo').color} 100%)`,
              '&:hover': { background: `linear-gradient(135deg, ${getTone('indigo').color} 0%, ${getTone('indigo').deep} 100%)` },
              '&.Mui-disabled': { background: (theme) => `${theme.palette.divider}`, color: 'text.disabled' }
            }}
          >
            {creating ? 'Création…' : 'Créer les questionnaires'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aperçu d'un questionnaire */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullWidth
        maxWidth="md"
        scroll="body"
        PaperProps={{ sx: { borderRadius: '24px' } }}
      >
        <DialogTitle sx={{ backgroundColor: 'action.hover', pb: 0, pt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>Aperçu du questionnaire</Typography>
            <IconButton onClick={() => setPreviewOpen(false)} sx={{ color: 'text.secondary' }}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: 'action.hover' }}>
          {previewLoading ? (
            <Box sx={{ p: 5, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <QuestionnairePreview questionnaire={previewData} questions={previewQuestions} />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: '12px', fontWeight: 700 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </motion.div>
  );
};

SessionQuestionnairesStatus.propTypes = {
  sessionId: PropTypes.any.isRequired
};

export default SessionQuestionnairesStatus;
